import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Workflow, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useWorkflows } from '../hooks/useWorkflows';
import { useTeams } from '../../rocks/hooks/useTeams';
import {
  useWorkflowTeamAssignments,
  useUpsertMastraAssignment,
  useDeleteMastraAssignment,
} from '../hooks/useMastraTeamAssignments';
import { WORKFLOW_DESCRIPTIONS } from '../data/workflow-metadata';
import type { WorkflowListItem, WorkflowsResponse } from '../types/workflow';

function formatWorkflowName(id: string): string {
  // Handle camelCase (e.g. "paymentDetectionWorkflow" -> "Payment Detection Workflow")
  const spaced = id.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced
    .split(/[-_ ]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function toListItems(
  data: WorkflowsResponse | undefined,
  assignmentMap: Record<string, string>,
  teamsMap: Record<string, string>,
): WorkflowListItem[] {
  if (!data) return [];
  return Object.entries(data).map(([id, wf]) => ({
    id,
    name: wf.name || formatWorkflowName(id),
    stepCount: wf.stepGraph?.length ?? Object.keys(wf.steps ?? {}).length,
    description: wf.description || WORKFLOW_DESCRIPTIONS[id] || '',
    teamId: assignmentMap[id],
    teamName: assignmentMap[id] ? teamsMap[assignmentMap[id]] : undefined,
  }));
}

export function WorkflowListPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useWorkflows();
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState<string>('all');

  const { data: teams } = useTeams();
  const { data: assignments } = useWorkflowTeamAssignments();
  const upsertAssignment = useUpsertMastraAssignment();
  const deleteAssignment = useDeleteMastraAssignment();

  const assignmentMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (assignments) {
      for (const a of assignments) {
        map[a.entity_id] = a.team_id;
      }
    }
    return map;
  }, [assignments]);

  const teamsMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (teams) {
      for (const t of teams) {
        map[t.id] = t.name;
      }
    }
    return map;
  }, [teams]);

  const items = useMemo(() => {
    let filtered = toListItems(data, assignmentMap, teamsMap);
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (w) =>
          w.name.toLowerCase().includes(q) ||
          w.id.toLowerCase().includes(q) ||
          w.description.toLowerCase().includes(q),
      );
    }
    if (teamFilter !== 'all') {
      filtered = filtered.filter((w) => w.teamId === teamFilter);
    }
    return filtered;
  }, [data, search, teamFilter, assignmentMap, teamsMap]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Workflow className="h-5 w-5 text-blue-400" />
          <h1 className="text-xl font-semibold">Workflows</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger className="w-[160px] h-9">
              <SelectValue placeholder="All Teams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {(teams || []).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search workflows..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-800 bg-red-950/50 p-4 text-sm text-red-300">
          Failed to load workflows. Is Mastra running?
        </div>
      )}

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>ID</TableHead>
              <TableHead className="w-40">Team</TableHead>
              <TableHead className="text-center w-20">Steps</TableHead>
              <TableHead>Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><div className="h-4 w-32 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell><div className="h-4 w-24 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell><div className="h-4 w-24 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell className="text-center"><div className="h-4 w-6 mx-auto animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell><div className="h-4 w-48 animate-pulse rounded bg-muted" /></TableCell>
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {search ? 'No workflows match your search.' : 'No workflows found.'}
                </TableCell>
              </TableRow>
            ) : (
              items.map((wf) => (
                <TableRow
                  key={wf.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/ai/workflows/${wf.id}`)}
                >
                  <TableCell className="font-medium text-blue-300">{wf.name}</TableCell>
                  <TableCell className="font-mono text-muted-foreground text-sm">{wf.id}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={wf.teamId || 'none'}
                      onValueChange={(value) => {
                        if (value === 'none') {
                          deleteAssignment.mutate({ entityType: 'workflow', entityId: wf.id });
                        } else {
                          upsertAssignment.mutate({ entityType: 'workflow', entityId: wf.id, teamId: value });
                        }
                      }}
                    >
                      <SelectTrigger className="w-[130px] h-7 text-xs">
                        <SelectValue placeholder="No team" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No team</SelectItem>
                        {(teams || []).map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-center">{wf.stepCount}</TableCell>
                  <TableCell className="text-muted-foreground">{wf.description}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
